import { SearchField } from "@/components/common/SearchField/SearchField"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQueryClient } from "@tanstack/react-query"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Loader2, Send, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  patchInquiryRequest,
  syncPatchedInquiryCaches,
  useCreateInquiries,
} from "../../../api/inquiryDraftApi"
import {
  useInquiryFrameworks,
  useInquiryRegulationOptions,
} from "../../../api/supplierInquiriesApi"
import { useResolveSuppliers, useSupplierList } from "../../../api/suppliersApi"
import { productStatusLabel } from "../../../constants/masterDataFilters"
import {
  inquiryRegulationLabels,
  regulationKeyFor,
} from "../../../utils/inquiryScope"
import { withContextParagraph } from "../../declarations/review/studio-v2/footer/requestContext"
import { FooterActionBar } from "../../shared/FooterActionBar"
import { McTable, McTd, McTr } from "../../shared/McTable"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { SupplierWarningBadge } from "../../shared/notes/SupplierWarningBadge"
import { SectionLabel } from "../../shared/SectionLabel"
import type { InquiryDraftItem } from "../inquiry/draft/inquiryDraftMock"
import { InquiryRegulationPicker } from "../inquiry/InquiryRegulationPicker"
import { inquiryDraftPath } from "../inquiry/routes"

/** Row height the virtualizer assumes: two text lines plus the cell padding. */
const ROW_HEIGHT = 53

/** The two row shapes this table renders: a resolved deep-link recipient and a
 *  row from the supplier list. Everything the row shows but `status`, which
 *  only the list carries. */
interface PickerRow {
  id: string
  name: string
  country?: string | null
  supplierNumber?: string | null
  articlesCount: number
  status?: string | null
  /** Warning notes on the supplier, from the list or the resolve payload. */
  activeWarningCount?: number
}

export function NewInquiryPage() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const notesEnabled = useFeatureFlag("MC_NOTES")
  const [searchParams] = useSearchParams()

  // Deep-link from the master-data list's bulk "Send inquiry" action
  // (SendInquiryPanel) — arrives as repeated ?supplierIds=<id> params and
  // pre-checks those rows instead of that panel calling the create API itself.
  const preselectedSupplierIds = useRef(
    searchParams.getAll("supplierIds"),
  ).current
  // Same deep-link shape, from Studio V2's "Anfordern" footer button — real
  // mc_articles ids (StudioV2Layout already resolves the worklist's article
  // NUMBERS against the declaration's own pool before building this link;
  // this create endpoint matches on id, not number) still open on the
  // framework the reviewer was stuck on, scoping the created questionnaire
  // instead of shipping all 4 frameworks' full article lists.
  const preselectedArticleIds = useRef(
    searchParams.getAll("articleIds"),
  ).current
  // The rest of Studio V2's review context (TUR-1211, Erik 02.09 bug 3 — see
  // `studio-v2/footer/requestContext.ts`): which inquiry framework(s) to
  // preselect instead of all of them, the declaration/statement the inquiry is
  // about (persisted as `source_*`), and the one "Bezug" line appended to the
  // mail draft. All optional; a plain visit reads none of them.
  const preselectedFrameworks = useRef(
    searchParams.getAll("frameworks"),
  ).current
  const sourceContext = useRef({
    declarationId: searchParams.get("declarationId"),
    framework: searchParams.get("framework"),
    statementId: searchParams.get("statementId"),
    line: searchParams.get("context"),
  }).current
  const qc = useQueryClient()

  const { data: frameworks = [] } = useInquiryFrameworks()

  // The regulations the inquiries ask (TUR-1730): the org's set preselected, or the
  // one regulation Studio's "Anfordern" came from. Only a changed set is sent: an
  // untouched one keeps the drafts following Settings › Regelwerke live (option B).
  // Nothing is created before the options load: a Studio link's pick must not
  // silently become the org default while they are loading or failed.
  const regulationOptionsQuery = useInquiryRegulationOptions()
  const regulationOptions = regulationOptionsQuery.data
  const settingsOn = regulationOptions?.settingsOn ?? false
  const orgKeys = useMemo(
    () =>
      (regulationOptions?.regulations ?? [])
        .filter((option) => option.inOrgSet)
        .map((option) => option.key),
    [regulationOptions],
  )
  // The regulations a link names; the regulation the reviewer stood on only when it
  // names none. A Studio request for what an inquiry still lacks names them itself,
  // and the declaration's own framework must not slip in beside them (TUR-1732).
  const deepLinkedRegulations = useRef(
    (searchParams.getAll("regulations").length
      ? searchParams.getAll("regulations")
      : [regulationKeyFor(searchParams.get("framework"))]
    ).filter((key): key is string => !!key),
  ).current
  const deepLinkKeys = useMemo(() => {
    const askable = new Set(
      (regulationOptions?.regulations ?? [])
        .filter((option) => option.pickable)
        .map((option) => option.key),
    )
    return [...new Set(deepLinkedRegulations.filter((key) => askable.has(key)))]
  }, [regulationOptions, deepLinkedRegulations])
  // A linked regulation the picker offers but cannot pick yet is not ticked; the
  // footer names it instead of dropping it silently.
  const unavailableLinkKeys = useMemo(() => {
    const blocked = new Set(
      (regulationOptions?.regulations ?? [])
        .filter((option) => !option.pickable)
        .map((option) => option.key),
    )
    return [...new Set(deepLinkedRegulations.filter((key) => blocked.has(key)))]
  }, [regulationOptions, deepLinkedRegulations])
  // Only a hand change (or a Studio link) is a pick; untouched ticks always show
  // the latest org set, so a refetch with new Settings never sends a stale copy.
  const [manualKeys, setManualKeys] = useState<string[] | null>(null)
  // "Reset to defaults" drops a Studio link's preselection too, back to the org set.
  const [linkDropped, setLinkDropped] = useState(false)
  const linkKeys = linkDropped ? [] : deepLinkKeys
  const regulationKeys =
    manualKeys ?? (linkKeys.length > 0 ? linkKeys : orgKeys)
  const pickedRegulations =
    settingsOn &&
    (manualKeys !== null || linkKeys.length > 0) &&
    regulationKeys.length > 0 &&
    (regulationKeys.length !== orgKeys.length ||
      regulationKeys.some((key) => !orgKeys.includes(key)))
      ? regulationKeys
      : undefined

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(preselectedSupplierIds),
  )
  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<string>>(
    new Set(),
  )
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)

  const seededFrameworks = useRef(false)
  useEffect(() => {
    if (seededFrameworks.current || frameworks.length === 0) return
    // A preselection from the deep link wins over "all", but only the values
    // the picker actually knows — an unknown token would 422 on create.
    const known = new Set(frameworks.map((fw) => fw.value))
    const wanted = preselectedFrameworks.filter((fw) => known.has(fw))
    setSelectedFrameworks(new Set(wanted.length > 0 ? wanted : known))
    seededFrameworks.current = true
  }, [frameworks, preselectedFrameworks])

  // Real suppliers from master data — the picker for adding more recipients.
  // Nothing loads on mount when the deep link already names the recipients:
  // the page opens on exactly those, and the list is fetched the moment the
  // user reaches for the picker (focusing the search field, or the "add more"
  // button that says the picker is there). Without a preselection the list IS
  // the page, so it loads immediately as before.
  const hasPreselection = preselectedSupplierIds.length > 0
  const [pickerOpen, setPickerOpen] = useState(!hasPreselection)
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSupplierList({
      q: debouncedSearch || undefined,
      limit: 200,
      enabled: pickerOpen,
    })
  const listSuppliers = data?.pages.flatMap((p) => p.items) ?? []

  // The deep link's recipients, resolved BY ID. This used to page the supplier
  // list in the background until every preselected id happened to turn up —
  // which on a large org meant walking the whole list to show rows the user
  // had already picked. They are pinned above the picker instead, so a
  // preselection is visible whatever page of the list it would sit on.
  const {
    data: preselectedSuppliers = [],
    isPending: isResolvingPreselection,
    isSuccess: preselectionResolved,
    isError: preselectionFailed,
  } = useResolveSuppliers(preselectedSupplierIds)
  const preselectedIds = useMemo(
    () => new Set(preselectedSuppliers.map((s) => s.id)),
    [preselectedSuppliers],
  )

  // A preselected id the resolve did not return no longer exists for this org
  // (deleted between selecting it and landing here). The table cannot show it,
  // so the count and what is submitted must not carry it either — an inquiry
  // for a recipient nobody can see on this page. Say it once, then drop it.
  const reconciledPreselection = useRef(false)
  useEffect(() => {
    if (reconciledPreselection.current || !preselectionResolved) return
    reconciledPreselection.current = true
    const missing = preselectedSupplierIds.filter(
      (id) => !preselectedIds.has(id),
    )
    if (missing.length === 0) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of missing) next.delete(id)
      return next
    })
    toast.warning(
      translate("MC_INQ_NEW_UNRESOLVED_SUPPLIERS", { n: missing.length }),
    )
  }, [preselectionResolved, preselectedIds, preselectedSupplierIds, translate])
  // Never twice: a pinned supplier that is also on a loaded page shows once.
  const pickerSuppliers = listSuppliers.filter((s) => !preselectedIds.has(s.id))
  // Pinned first, then the picker. A resolved row carries no workflow status:
  // that needs the list's full enrichment, and resolving a deep link's
  // recipients has to stay one cheap read.
  const rows: PickerRow[] = [...preselectedSuppliers, ...pickerSuppliers]

  // A selection can name more recipients than fit on a screen, so the body is
  // virtualized against McTable's own scroll container (padding rows keep the
  // <table> valid — same approach as the QMS pivot table).
  const scrollRef = useRef<HTMLDivElement>(null)
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  })
  const virtualRows = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0

  // Creating the drafts happens HERE (once), then we navigate to the draft page by
  // the new inquiry ids — so reloading the draft page re-loads, never re-creates.
  const createInquiries = useCreateInquiries()

  // Select-all covers what is on screen: the pinned recipients plus the picker
  // rows loaded so far.
  const visibleIds = [
    ...preselectedSuppliers.map((s) => s.id),
    ...pickerSuppliers.map((s) => s.id),
  ]
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someSelected =
    !allSelected && visibleIds.some((id) => selectedIds.has(id))

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(visibleIds))
    }
  }

  const tableHeaders = [
    <Checkbox
      key="select-all"
      checked={someSelected ? "indeterminate" : allSelected}
      onCheckedChange={toggleAll}
      aria-label={translate("MC_INQ_SELECT_ALL")}
    />,
    translate("MC_DECL_COL_SUPPLIER"),
    translate("MC_DECL_COL_STATUS"),
  ]

  return (
    <div data-agent-target="inquiry.new" className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pb-4 pt-6">
        <ModuleHeader
          title={translate("MC_INQ_NEW_TITLE")}
          subtitle={translate("MC_INQ_NEW_SUBTITLE")}
          helpArticle={{
            categoryId: "supplier-studio",
            articleId: "abfrage-erstellen",
          }}
          right={
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <X size={16} />
            </Button>
          }
        />

        {/* Focus anywhere in here means the user is reaching for the picker. */}
        <div
          className="flex items-center gap-3"
          onFocusCapture={() => setPickerOpen(true)}
        >
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={translate("MC_INQ_NEW_SEARCH_PLACEHOLDER")}
            onClear={() => setSearch("")}
          />
          {!pickerOpen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
            >
              {translate("MC_INQ_NEW_ADD_MORE_SUPPLIERS")}
            </Button>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <McTable
            headers={tableHeaders}
            stickyHeader
            scrollRef={scrollRef}
            // The body is virtualized, so the mounted <tr>s are not the count.
            // +1 for the header row, which aria-rowcount includes.
            ariaRowCount={rows.length + 1}
            className="h-full bg-grey3"
          >
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableHeaders.length}
                    className="px-3 py-8 text-center text-sm text-grey5"
                  >
                    {hasPreselection && preselectionFailed
                      ? translate("MC_INQ_SEND_RESOLVE_ERROR")
                      : isLoading ||
                          (hasPreselection && isResolvingPreselection)
                        ? translate("MC_INQ_NEW_LOADING_ROWS")
                        : translate("MC_INQ_NEW_EMPTY")}
                  </td>
                </tr>
              ) : (
                <>
                  {paddingTop > 0 && (
                    // Height only: without this the table's aria-rowcount would
                    // be read out with two empty rows inside it.
                    <tr aria-hidden="true">
                      <td
                        colSpan={tableHeaders.length}
                        style={{ height: paddingTop, padding: 0, border: 0 }}
                      />
                    </tr>
                  )}
                  {virtualRows.map((virtualRow) => {
                    const supplier = rows[virtualRow.index]
                    const isSelected = selectedIds.has(supplier.id)
                    return (
                      <McTr
                        key={supplier.id}
                        rowIndex={virtualRow.index + 2}
                        onClick={() => toggleRow(supplier.id)}
                        className={isSelected ? "bg-primary/10" : undefined}
                      >
                        <McTd className="w-10">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleRow(supplier.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={translate("MC_INQ_SELECT_SUPPLIER", {
                              name: supplier.name,
                            })}
                          />
                        </McTd>
                        <McTd>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[12.5px] font-medium text-white">
                              {supplier.name}
                              {notesEnabled && supplier.activeWarningCount ? (
                                <SupplierWarningBadge
                                  count={supplier.activeWarningCount}
                                  className="ml-1.5 align-text-bottom"
                                />
                              ) : null}
                            </span>
                            <span className="text-[11.5px] text-grey5">
                              {[supplier.country, supplier.supplierNumber]
                                .filter(Boolean)
                                .join(" · ")}
                              {" · "}
                              {translate("MC_INQ_NEW_ARTICLES", {
                                n: supplier.articlesCount,
                              })}
                            </span>
                          </div>
                        </McTd>
                        <McTd>
                          <span className="text-[12px] text-grey5">
                            {supplier.status
                              ? productStatusLabel(supplier.status, translate)
                              : "—"}
                          </span>
                        </McTd>
                      </McTr>
                    )
                  })}
                  {paddingBottom > 0 && (
                    <tr aria-hidden="true">
                      <td
                        colSpan={tableHeaders.length}
                        style={{ height: paddingBottom, padding: 0, border: 0 }}
                      />
                    </tr>
                  )}
                </>
              )}
              {hasNextPage && (
                <tr>
                  <td
                    colSpan={tableHeaders.length}
                    className="py-3 text-center"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isFetchingNextPage}
                      onClick={() => fetchNextPage()}
                    >
                      {isFetchingNextPage
                        ? translate("MC_INQ_DRAFT_ADD_ARTICLE_LOADING")
                        : translate("MC_INQ_DRAFT_ADD_ARTICLE_LOAD_MORE")}
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </McTable>
        </div>
      </div>

      <FooterActionBar
        left={
          regulationOptionsQuery.isError ? (
            <div role="alert" className="flex items-center gap-3 text-[12px]">
              <span className="text-warning">
                {translate("MC_INQ_ERR_REGULATION_SETTINGS_UNAVAILABLE")}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void regulationOptionsQuery.refetch()}
              >
                {translate("MC_DECL_BTN_RETRY")}
              </Button>
            </div>
          ) : settingsOn && regulationOptions ? (
            <div className="flex flex-col gap-2">
              <SectionLabel>
                {translate("MC_INQ_FRAMEWORKS_LABEL")}
              </SectionLabel>
              <InquiryRegulationPicker
                options={regulationOptions.regulations}
                value={regulationKeys}
                onChange={setManualKeys}
                onFollowSettings={
                  pickedRegulations
                    ? () => {
                        setManualKeys(null)
                        setLinkDropped(true)
                      }
                    : undefined
                }
                disabled={createInquiries.isPending}
              />
              {!linkDropped && unavailableLinkKeys.length > 0 && (
                <span role="status" className="text-[11.5px] text-warning">
                  {translate("MC_INQ_REG_LINK_UNAVAILABLE", {
                    list: inquiryRegulationLabels(
                      unavailableLinkKeys,
                      translate,
                    )
                      .map((regulation) => regulation.title)
                      .join(", "),
                  })}
                </span>
              )}
            </div>
          ) : undefined
        }
      >
        <Button variant="outline" onClick={() => navigate(-1)}>
          {translate("MC_DECL_BTN_CANCEL")}
        </Button>
        <Button
          // A deep link seeds the selection from the RAW url ids, so until the
          // resolve confirms them this page would create inquiries for ids it
          // has not checked, including ones the org cannot resolve at all (the
          // reconciliation above runs only once the resolve lands). A failed
          // resolve never confirms anything, so it stays closed and the table
          // says to reload.
          disabled={
            selectedIds.size === 0 ||
            createInquiries.isPending ||
            (hasPreselection && !preselectionResolved) ||
            !regulationOptionsQuery.isSuccess
          }
          onClick={async () => {
            let inquiryIds: string[]
            try {
              // The legacy labels (never drive documents) default to all known ones;
              // what the documents ask is `regulationKeys` above (TUR-1730).
              const frameworksToSubmit =
                selectedFrameworks.size > 0
                  ? [...selectedFrameworks]
                  : frameworks.map((fw) => fw.value)
              inquiryIds = await createInquiries.mutateAsync({
                supplierIds: [...selectedIds],
                frameworks: frameworksToSubmit,
                ...(pickedRegulations
                  ? { regulationKeys: pickedRegulations }
                  : {}),
                ...(preselectedArticleIds.length
                  ? { articleIds: preselectedArticleIds }
                  : {}),
                ...(sourceContext.declarationId
                  ? {
                      triggerType: "declaration_review" as const,
                      triggerDetail: sourceContext.line ?? undefined,
                      sourceDeclarationId: sourceContext.declarationId,
                      sourceFramework: sourceContext.framework ?? undefined,
                      sourceStatementId: sourceContext.statementId ?? undefined,
                    }
                  : {}),
              })
            } catch {
              // The mutation's onError already surfaced a toast; stay on the page.
              return
            }
            // The "Bezug" line goes INTO the mail draft, as a saved body the
            // composer shows and the send uses, PREPENDED before the template
            // the create just resolved for the draft's language (tester finding,
            // Pablo/Notion item 8: appended at the bottom, he never noticed
            // which declaration it was about), so it's the first thing the
            // reviewer reads and can still edit. Best-effort: a failed patch
            // leaves a normal draft, never blocks the navigation.
            const contextLine = sourceContext.line
            if (contextLine) {
              const label = translate("MC_INQ_CONTEXT_LABEL")
              await Promise.all(
                inquiryIds.map(async (id) => {
                  const item = qc.getQueryData<InquiryDraftItem>([
                    "mc",
                    "inquiries",
                    "detail",
                    id,
                  ])
                  if (!item) return
                  const lang = item.language === "de" ? "de" : "en"
                  const body = item.emailCopy.first[lang].body
                  try {
                    const saved = await patchInquiryRequest(id, {
                      emailBody: withContextParagraph(body, label, contextLine),
                    })
                    syncPatchedInquiryCaches(qc, id, saved)
                  } catch {
                    // Non-critical: the draft is still complete without the line.
                  }
                }),
              )
            }
            navigate(inquiryDraftPath(inquiryIds))
          }}
        >
          {createInquiries.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Send size={14} />
          )}
          {translate("MC_INQ_NEW_BTN_CONTINUE_DRAFT")}
          {selectedIds.size > 0 && ` (${selectedIds.size})`}
        </Button>
      </FooterActionBar>
    </div>
  )
}
